import ExternalIconLink from 'components/links/ExternalIconLink';
import { Link } from 'gatsby';
import React from 'react';
import styled from 'styled-components';
import { socialMedia, ISocialPresence } from 'components/footer-social-media';
import LogoSvg from 'images/main/logo.inline.svg';
import CartDucky from 'images/homepage/CartDucky';

const row2Links: ILinkProps[] = [
    {
        heading: 'Open Source',
        links: [
            {
                text: 'Mobile',
                href: 'https://github.com/ente-io/photos-app',
                target: '_blank',
            },
            {
                text: 'Web',
                href: 'https://github.com/ente-io/photos-web',
                target: '_blank',
            },
            {
                text: 'Desktop',
                href: 'https://github.com/ente-io/photos-desktop',
                target: '_blank',
            },
            {
                text: 'CLI',
                href: 'https://github.com/ente-io/cli',
                target: '_blank',
            },
        ],
    },
    {
        heading: 'Products',
        links: [
            { text: 'Photos', href: '/download' },
            {
                text: 'Auth',
                href: 'https://github.com/ente-io/auth',
                target: '_blank',
            },
            {
                text: 'Locker',
                href: 'https://locker.ente.io',
                target: '_blank',
                label: 'Coming Soon',
            },
            { text: 'Legacy', label: 'Coming Soon' },
        ],
    },
    {
        heading: 'Features',
        links: [
            { text: 'Family plans', href: '/blog/family-plans' },
            { text: 'Sharing', href: '/faq/features/share/' },
            { text: 'Collaboration', href: '/blog/collaborative-albums' },
            { text: 'Magic Search', label: 'Coming Soon' },
        ],
    },
    {
        heading: 'Technology',
        links: [
            { text: 'Encryption', href: '/architecture' },
            { text: 'Replication', href: '/reliability' },
            { text: 'Edge ML', href: '/blog/image-search-with-clip-ggml/' },
        ],
    },
    {
        heading: 'Compare',
        links: [
            {
                text: 'Google Photos vs Ente',
                href: '/compare/ente-vs-google-photos/',
            },
            {
                text: 'Apple Photos vs Ente',
                href: '/compare/ente-vs-apple-photos/',
            },
            { text: 'Dropbox vs Ente', href: '/compare/ente-vs-dropbox/' },
        ],
    },
];

const Container = styled.section.attrs({
    className: 'container g-0',
})`
    border-radius: 40px 40px 0 0;
    margin-top: 150px;
    background-color: #0f0f0f;

    & *,
    & a {
        color: #fff !important;
    }

    @media (prefers-color-scheme: dark) {
        background-color: #fff;

        & *,
        & a {
            color: #111 !important;
        }
    }
`;

const FooterLinks: React.FC = () => {
    return (
        <Container>
            <div className="ps-4 pe-4 ps-md-5 pe-md-5 pt-6 pb-5 pb-md-4 gap-0">
                <DescriptionAndSocialLinks />
                <div className="d-flex flex-wrap">
                    {row2Links.map((links) => (
                        <Links {...links} />
                    ))}
                </div>
                <GeneralAndLegal />
            </div>
        </Container>
    );
};

export default FooterLinks;

// ---- Row 1 -----

const FooterDucky = styled.div`
    margin-top: -60px;
    flex: 1;
    display: flex;
    justify-content: center;

    & > svg {
        max-width: 100%;
        transform: scale(1.3);
        margin-bottom: 20px;
    }

    @media (min-width: 992px) {
        justify-content: end;
        & > svg {
            margin-right: 60px;
        }
    }
`;

const DescriptionAndSocialLinks: React.FC = () => {
    return (
        <div className="d-flex flex-wrap flex-column-reverse flex-lg-row mb-5">
            <Description />
            <FooterDucky>
                <CartDucky />
            </FooterDucky>
        </div>
    );
};

const Description: React.FC = () => {
    return (
        <div className="d-flex flex-column align-items-start g-0">
            <Link to="/" aria-label="Home">
                <LogoSvg />
            </Link>
            <DescriptionText />
            <SocialLinks />
        </div>
    );
};

const DescriptionText: React.FC = () => {
    return (
        <FooterText>
            Ente is a cloud based mobile and desktop photos app with a focus on
            security and privacy.
        </FooterText>
    );
};

const FooterText = styled.div`
    opacity: 0.8;
    font-weight: 600;
    max-width: 500px;
    margin-bottom: 20px;
    margin-top: 8px;
`;

const SocialLinks: React.FC = () => {
    return (
        <SocialLinkDiv className="d-flex flex-wrap justify-content-md-end align-items-start align-content-center gap-4">
            {socialMedia.map((item) => (
                <SocialLink key={item.url} {...item} />
            ))}
        </SocialLinkDiv>
    );
};

/* 
  For phones smaller than around iPhone 8 - the social links start wrapping.
  However, initially only one icon wraps over to the next row, which does not
  look visually pleasing.

  As a workaround, when we hit these sized screens, limit the max-width of the
  div so that when the wrapping does occur, multiple icons get wrapped over to
  the next row (instead of only one).
  */
const SocialLinkDiv = styled.div`
    @media (max-width: 340px) {
        max-width: 85%;
    }
`;

const SocialLink: React.FC<ISocialPresence> = ({ url, name, icon }) => {
    return (
        <ExternalIconLink
            href={url}
            title={name}
            rel="me"
            className={'link-light link-footer'}>
            {icon}
        </ExternalIconLink>
    );
};

// ---- Row 2 -----

interface ILinkProps {
    heading: string;
    links: {
        text: string;
        href?: string;
        label?: string;
        target?: string;
    }[];
}

const LinkHeader = styled.strong`
    color: #5f5f5f;
    font-weight: 700;
    font-size: 18px;
    margin-top: 20px;
    margin-bottom: 10px;
`;

const LinkExt = styled.a`
    color: #fff;
    text-decoration: none;
    margin: 10px 0;
    cursor: pointer;

    &:hover {
        color: #2bc260;
    }
`;

const Row2 = styled.div.attrs({
    className: 'd-flex flex-column align-items-start',
})`
    flex: 1;
    min-width: 200px;
`;

const Label = styled.span`
    padding: 4px 8px;
    background: rgba(43, 189, 94, 0.2);
    color: #1db954 !important;
    font-size: 10px;
    border-radius: 12px;
    margin-left: 10px;
    font-weight: 700;
`;

const Links = ({ heading, links }: ILinkProps) => {
    return (
        <Row2>
            <LinkHeader>{heading}</LinkHeader>
            {links.map((link) => (
                <LinkExt href={link.href} target={link.target}>
                    {link.text}
                    {link.label && <Label>{link.label}</Label>}
                </LinkExt>
            ))}
        </Row2>
    );
};

// ---- Row 3 -----

const GeneralAndLegal: React.FC = () => {
    return (
        // This row is smaller than the other two, so it needs an extra top
        // margin to make it fit nicer.
        <div className="vstack gap-2 mt-5">
            <NavigationAndLegal />
        </div>
    );
};

const NavigationAndLegal: React.FC = () => {
    return (
        <div className="d-flex flex-wrap justify-content-between gap-4">
            <NavigationLinks />
            <LegalAndPressLinks />
        </div>
    );
};

const NavigationLinks: React.FC = () => {
    return (
        <div className="d-flex flex-wrap gap-3 gap-md-4">
            <FooterLink to="/faq">FAQ</FooterLink>
            <FooterLink to="/contact">Contact</FooterLink>
            <FooterLink to="/#pricing">Pricing</FooterLink>
            <FooterLink to="/blog">Blog</FooterLink>
            <FooterLink to="/compare">Compare</FooterLink>
            <FooterLink to="/articles">Articles</FooterLink>
            <FooterLink to="https://shop.ente.io">Shop</FooterLink>
        </div>
    );
};

const LegalAndPressLinks: React.FC = () => {
    return (
        <small>
            <div className="d-flex flex-wrap gap-3">
                <FooterSecondaryLink to="/privacy">Privacy</FooterSecondaryLink>
                <FooterSecondaryLink to="/terms">Terms</FooterSecondaryLink>
                <FooterSecondaryLink to="/press">Press Kit</FooterSecondaryLink>
            </div>
        </small>
    );
};

/**
 * Variant of the primary link without the underline. Instead, color is used to
 * indicate hover.
 */
const FooterLink = styled(Link).attrs({
    className: 'link-footer text-light',
})``;

/**
 * Variants of the secondary links that use a different color for indicating
 * hover in dark mode.
 */
const FooterSecondaryLink = styled(Link)`
    opacity: 0.7;
    color: #fff;
    text-decoration: none;

    &:hover {
        color: #fff;
        opacity: 1;
    }
`;
